import { YesNoState } from '@alfa-retail-react/components/yes-no-question';
import { action, computed, makeObservable, observable } from 'mobx';

import { InsuranceListStore } from 'app/logic/pages/productParametersConfigure/InsuranceListStore';
import { CardOrder } from 'app/model/CardOrder';
import { CardProduct } from 'app/model/product/CardProduct';
import { InsuranceProduct } from 'app/model/product/InsuranceProduct';
import { Employment } from 'app/model/Employment';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/FeatureFlags';

export class ProductParametersConfigureFormStore {
    @observable
    insuranceStore: InsuranceListStore;

    @observable
    creditLimit: string;

    @observable
    insuranceProduct: InsuranceProduct;

    @observable
    private _insuranceAppliedState: YesNoState | null;

    private _insuranceRequired: boolean;

    @observable
    private _cardProduct: CardProduct;

    @computed
    get availableInsuranceList() {
        return this.insuranceStore.sortedInsuranceList.filter(
            (insuranceProduct) => {
                return (
                    insuranceProduct.minCreditAmount <= Number(this.creditLimit)
                );
            },
        );
    }

    @computed
    get displayInsuranceList() {
        return this.hasAvailableInsurance
            ? this.availableInsuranceList
            : this.insuranceStore.minAmountInsuranceProducts;
    }

    @computed
    get defaultInsurance() {
        const insurancePool = this.hasAvailableInsurance
            ? this.availableInsuranceList
            : this.insuranceStore.minAmountInsuranceProducts;

        if (insurancePool.length === 0) {
            return;
        }

        return insurancePool.reduce((insurance, item) => {
            return item.amount > insurance.amount ? item : insurance;
        });
    }

    @computed
    get hasAvailableInsurance(): boolean {
        return !!this.availableInsuranceList.length;
    }

    @computed
    get hasAppliedInsurance() {
        return this._insuranceAppliedState === YesNoState.YES;
    }

    get insuranceRequired() {
        return this._insuranceRequired;
    }

    @computed
    get insuranceAppliedState() {
        return this._insuranceAppliedState;
    }

    @action
    setInsurance(insurance: InsuranceProduct) {
        this.insuranceProduct = insurance;
    }

    @action
    setHasInsurance(hasInsurance: YesNoState | null) {
        this._insuranceAppliedState = hasInsurance;
    }

    @action
    setCreditLimit(limit: string) {
        this.creditLimit = limit;
    }

    constructor(cardProduct: CardProduct) {
        makeObservable(this);

        this._cardProduct = cardProduct;
        this.insuranceStore = new InsuranceListStore(cardProduct.insurance);
        this._insuranceRequired = cardProduct.insuranceRequired;
        this.creditLimit = String(cardProduct.maxAmount);

        this.setInsurance(this.defaultInsurance);

        if (isFeatureEnabled(FeatureFlag.INSURANCE_NEW_VIEW)) {
            this.setHasInsurance(null);

            return;
        }

        const defaultHasInsurance = cardProduct.insuranceEnabled
            ? YesNoState.YES
            : YesNoState.NO;

        this.setHasInsurance(defaultHasInsurance);
    }

    static default(cardProduct: CardProduct, creditLimit: string) {
        const formStore = new ProductParametersConfigureFormStore(cardProduct);
        formStore.setCreditLimit(creditLimit);

        return formStore;
    }

    static fromOrder(cardProduct: CardProduct, order: CardOrder) {
        const formStore = new ProductParametersConfigureFormStore(cardProduct);
        const { insuranceProductCode, amount } = order;

        // В новом виде выбор страховки из заказа не восстанавливается
        if (!isFeatureEnabled(FeatureFlag.INSURANCE_NEW_VIEW)) {
            const insuranceProduct =
                !!insuranceProductCode &&
                formStore.insuranceStore.getInsuranceByCode(
                    insuranceProductCode,
                );

            if (insuranceProduct) {
                formStore.setInsurance(insuranceProduct);
                formStore.setHasInsurance(YesNoState.YES);
            } else {
                formStore.setHasInsurance(YesNoState.NO);
            }
        }

        const activityKindCode = order.client.employment?.activityKindCode;
        const productMaxAmount =
            ProductParametersConfigureFormStore.getMaxAmount({
                cardProduct,
                activityKindCode,
            });
        const fixedAmount =
            Number(amount) > Number(productMaxAmount)
                ? productMaxAmount
                : amount;

        formStore.setCreditLimit(String(fixedAmount));

        return formStore;
    }

    static getMaxAmount({
        cardProduct,
        activityKindCode,
    }: {
        cardProduct: CardProduct;
        activityKindCode: string;
    }) {
        if (!cardProduct) {
            return null;
        }

        const isSoleTrader = Employment.isActivityHasUNP(activityKindCode);
        const isSelfEmployed =
            activityKindCode === Employment.ActivityCode.selfEmployed;

        if (!isSoleTrader) {
            return cardProduct.maxAmount;
        }

        const soleMaxAmount = isSelfEmployed
            ? cardProduct.selfEmployedMaxAmount
            : cardProduct.soleTradersMaxAmount;

        if (soleMaxAmount.toNumber() > cardProduct.maxAmount.toNumber()) {
            return cardProduct.maxAmount;
        }

        return soleMaxAmount !== null ? soleMaxAmount : cardProduct.maxAmount;
    }
}
